import React, { useCallback, useState } from 'react';
import PhotoUploadModal from '../components/ui/PhotoUploadModal';
import { uploadPlayerImage, removePlayerImage } from '../lib/supabase';
import { useConfirmDialog } from './useConfirmDialog';

// La foto del jugador se cambiaba SOLO desde la ficha del grupo: toda la orquestacion
// (a quien, subiendo, exito, cerrar) vivia como estado suelto dentro de GroupDetails,
// que ya pasa de 2,200 lineas. El perfil del jugador -- el sitio donde uno esperaria
// gestionarla -- mostraba un boton de camara con el onClick vacio.
//
// Este hook es esa orquestacion, en un solo lugar, con el patron que el repo ya usa en
// useConfirmDialog: devuelve el elemento listo para pintar. La interfaz sigue siendo
// PhotoUploadModal, que no se toca.
//
// `onDone` corre despues de cambiar o quitar la foto: cada pantalla recarga lo suyo.
export function usePhotoUpload({ onDone }) {
  const { alert, ConfirmDialog } = useConfirmDialog();
  const [jugador, setJugador] = useState(null);
  const [subiendoId, setSubiendoId] = useState(null);
  const [exito, setExito] = useState(null);

  const cerrar = useCallback(() => {
    setJugador(null);
    setExito(null);
  }, []);

  const abrir = useCallback((p) => {
    setExito(null);
    setJugador(p);
  }, []);

  const subir = useCallback(async (file) => {
    if (!file || !jugador?.id) return;
    try {
      setSubiendoId(jugador.id);
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.readAsDataURL(file);
      });

      const { data, error } = await uploadPlayerImage(jugador.id, {
        image_base64: base64,
        filename: file.name,
      });
      if (error) throw error;

      // La confirmacion se ve un momento y el modal se va solo: el usuario acaba de
      // elegir el archivo, no hay nada mas que decidir ahi.
      setExito({ player: jugador, url: data?.public_url || null });
      setTimeout(cerrar, 2000);
      await onDone?.();
    } catch (error) {
      console.error('[usePhotoUpload] subir', error);
      cerrar();
      await alert('Error', 'No se pudo subir la foto');
    } finally {
      setSubiendoId(null);
    }
  }, [jugador, onDone, cerrar, alert]);

  const quitar = useCallback(async (p) => {
    const objetivo = p || jugador;
    if (!objetivo?.id) return;
    try {
      setSubiendoId(objetivo.id);
      const { error } = await removePlayerImage(objetivo.id);
      if (error) throw error;

      setExito({ player: objetivo, url: null });
      setTimeout(cerrar, 1500);
      await onDone?.();
    } catch (error) {
      console.error('[usePhotoUpload] quitar', error);
      cerrar();
      await alert('Error', 'No se pudo quitar la foto');
    } finally {
      setSubiendoId(null);
    }
  }, [jugador, onDone, cerrar, alert]);

  const PhotoDialog = (
    <>
      <PhotoUploadModal
        isOpen={!!jugador}
        onClose={cerrar}
        player={jugador}
        uploading={subiendoId === jugador?.id}
        uploadSuccess={exito}
        onFileSelect={subir}
        onCameraCapture={subir}
        onRemove={quitar}
      />
      <ConfirmDialog />
    </>
  );

  return { abrir, cerrar, PhotoDialog, subiendoId };
}
